import { useNavigate } from "react-router-dom";
import { useSelectedCountry } from "@/context/SelectedCountryContext";
import { ArrowRight } from "lucide-react";

// Slug → ISO-3166 alpha-2 — used by flagcdn.com for crisp SVG flags
// (emoji flags don't render on Windows browsers).
const ISO = {
  "japan": "jp",
  "singapore": "sg",
  "united-kingdom": "gb",
  "united-states": "us",
  "uae": "ae",
  "germany": "de",
  "australia": "au",
  "france": "fr",
  "canada": "ca",
  "thailand": "th",
  "vietnam": "vn",
  "saudi-arabia": "sa",
};

export default function Home() {
  const { countries, setSlug } = useSelectedCountry();
  const navigate = useNavigate();

  const pick = (slug) => {
    setSlug(slug);
    navigate(`/countries/${slug}`);
  };

  return (
    <div data-testid="home-page" className="fade-in min-h-[80vh] max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
      <div className="mb-10 lg:mb-14 max-w-4xl">
        <div
          data-testid="home-tagline"
          className="font-display font-black tracking-tight text-[#0F172A] leading-[1.05] text-3xl sm:text-5xl lg:text-6xl uppercase"
        >
          Travel like the world is watching <span className="text-[#002FA7]">— because it is.</span>
        </div>
      </div>

      <div className="mb-12 lg:mb-16 max-w-2xl">
        <div className="overline mb-3">Choose your destination</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
          Where are you flying?
        </h1>
        <p className="mt-4 text-[#475569] text-base lg:text-lg leading-relaxed">
          Pick a country to begin. Everything else on the site — guides, locals,
          visa, quiz, checklist — will tailor itself to that destination.
        </p>
      </div>

      {countries.length === 0 ? (
        <div className="text-[#475569] py-12">Loading destinations…</div>
      ) : (
        <div
          data-testid="home-country-grid"
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
        >
          {countries.map((c) => {
            const iso = ISO[c.slug];
            return (
              <button
                key={c.slug}
                data-testid={`home-country-${c.slug}`}
                onClick={() => pick(c.slug)}
                className="group flex flex-col items-start gap-3 p-5 lg:p-6 rounded-sm border border-[#E2E8F0] bg-white hover:border-[#002FA7] hover:bg-[#EEF2FF] hover:-translate-y-0.5 transition-all text-left"
              >
                {iso ? (
                  <img
                    src={`https://flagcdn.com/w160/${iso}.png`}
                    srcSet={`https://flagcdn.com/w160/${iso}.png 1x, https://flagcdn.com/w320/${iso}.png 2x`}
                    width={64}
                    height={48}
                    alt={`${c.name} flag`}
                    data-testid={`flag-${c.slug}`}
                    className="w-16 h-12 object-cover rounded-sm shadow-sm ring-1 ring-black/5"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-4xl lg:text-5xl leading-none">{c.flag}</span>
                )}
                <span className="font-display font-bold text-[#0F172A] tracking-tight text-base lg:text-lg">
                  {c.name}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-[#475569] group-hover:text-[#002FA7]">
                  Open guide <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
